"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { UserPlus, Mail, User, Phone, Lock, AlertCircle, CheckCircle, ArrowLeft, Eye, EyeOff } from "lucide-react"
import "../styles/cadastro.css"

const Cadastro = () => {
  const [formData, setFormData] = useState({
    nomeCompleto: "",
    email: "",
    cpf: "",
    whatsapp: "",
    senha: "",
    confirmarSenha: "",
    tipo: "cliente",
  })

  const [erro, setErro] = useState("")
  const [sucesso, setSucesso] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [capsLockAtivo, setCapsLockAtivo] = useState(false);



  // Efeito para animação de partículas
  useEffect(() => {
    const canvas = document.getElementById("particles-cadastro")
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const particles = []
    const particleCount = 50

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        color: "rgba(255, 193, 7, " + (Math.random() * 0.3 + 0.2) + ")",
        speedX: Math.random() * 0.5 - 0.25,
        speedY: Math.random() * 0.5 - 0.25,
      })
    }

    function animate() {
      requestAnimationFrame(animate)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      for (let i = 0; i < particleCount; i++) {
        const p = particles[i]
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2)
        ctx.fillStyle = p.color
        ctx.fill()

        p.x += p.speedX
        p.y += p.speedY

        if (p.x < 0 || p.x > canvas.width) p.speedX *= -1
        if (p.y < 0 || p.y > canvas.height) p.speedY *= -1
      }
    }

    animate()

    const handleResize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Função para formatar CPF
  const formatCPF = (value) => {
    value = value.replace(/\D/g, "").slice(0, 11)
    return value.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, p1, p2, p3, p4) => {
      let result = `${p1}.${p2}.${p3}`
      if (p4) result += `-${p4}`
      return result
    })
  }

  // Função para formatar telefone
  const formatPhone = (value) => {
    value = value.replace(/\D/g, "").slice(0, 11)
    return value.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, p1, p2, p3) => {
      let result = `(${p1}) ${p2}`
      if (p3) result += `-${p3}`
      return result
    })
  }

  // Atualiza os valores dos inputs
  const handleChange = (e) => {
    let { name, value } = e.target
    if (name === "cpf") value = formatCPF(value)
    if (name === "whatsapp") value = formatPhone(value)
    setFormData({ ...formData, [name]: value })
  }

  const validarCPF = (cpf) => {
    cpf = cpf.replace(/[^\d]+/g, "") // remove tudo que não é número
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false

    let soma = 0
    for (let i = 0; i < 9; i++) soma += Number.parseInt(cpf.charAt(i)) * (10 - i)
    let resto = (soma * 10) % 11
    if (resto === 10 || resto === 11) resto = 0
    if (resto !== Number.parseInt(cpf.charAt(9))) return false

    soma = 0
    for (let i = 0; i < 10; i++) soma += Number.parseInt(cpf.charAt(i)) * (11 - i)
    resto = (soma * 10) % 11
    if (resto === 10 || resto === 11) resto = 0
    return resto === Number.parseInt(cpf.charAt(10))
  }

  const validarEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return regex.test(email)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErro("")
    setSucesso("")
    setIsLoading(true)

    if (!validarCPF(formData.cpf)) {
      setErro("CPF inválido!")
      setIsLoading(false)
      return
    }

    if (!validarEmail(formData.email)) {
      setErro("E-mail inválido!")
      setIsLoading(false)
      return
    }

    if (formData.senha !== formData.confirmarSenha) {
      setErro("As senhas não coincidem!")
      setIsLoading(false)
      return
    }
    if (/[A-Z]/.test(formData.email)) {
      setErro("O e-mail deve estar em letras minúsculas.");
      setIsLoading(false);
      return;
    }

    try {
    const response = await axios.post(
      "https://api.ticketdovaqueiro.com/api/auth/register",
      formData
    );

    // se o backend mandou token, já loga
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
    }

    setSucesso(response.data.mensagem || "Cadastro realizado com sucesso!");

    // redireciona para a página que ele estava antes
    navigate(from, { replace: true });

      
    } catch (error) {
      setErro(error.response?.data?.mensagem || "Erro ao registrar!")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="cadastro-page14">
      <canvas id="particles-cadastro" className="particles-canvas"></canvas>

      <div className="cadastro-overlay14">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="cadastro-container14"
        >
          <div className="cadastro-header14">
            <motion.div whileHover={{ x: -5 }} className="back-button14">
              <Link to="/login">
                <ArrowLeft size={20} />
                <span>Voltar para login</span>
              </Link>
            </motion.div>

            <motion.h1
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="cadastro-title14"
            >
              <UserPlus className="cadastro-icon14" />
              Crie sua conta
            </motion.h1>
          </div>

          <div className="cadastro-content14">
            <AnimatePresence>
              {erro && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="cadastro-error14"
                >
                  <AlertCircle size={18} />
                  {erro}
                </motion.div>
              )}

              {sucesso && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="cadastro-success14"
                >
                  <CheckCircle size={18} />
                  {sucesso}
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="cadastro-form14">
              <div className="form-grid14">
                <div className="form-group14">
                  <label htmlFor="nomeCompleto">Nome Completo</label>
                  <div className="input-wrapper14">
                    <User className="input-icon14" />
                    <input
                      type="text"
                      id="nomeCompleto"
                      name="nomeCompleto"
                      placeholder="Digite seu nome completo"
                      value={formData.nomeCompleto}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group14">
                  <label htmlFor="email">E-mail</label>
                  <div className="input-wrapper14">
                    <Mail className="input-icon14" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Digite seu e-mail"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      onKeyUp={(e) => setCapsLockAtivo(e.getModifierState("CapsLock"))}
                    />
                  </div>
                  {capsLockAtivo && (
  <span style={{ color: "orange", fontSize: "12px", marginTop: "4px" }}>
    ⚠️ CAPS LOCK está ativado
  </span>
)}

                </div>

                <div className="form-group14">
                  <label htmlFor="cpf">CPF</label>
                  <div className="input-wrapper14">
                    <User className="input-icon14" />
                    <input
                      type="text"
                      id="cpf"
                      name="cpf"
                      placeholder="Digite seu CPF"
                      value={formData.cpf}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group14">
                  <label htmlFor="whatsapp">WhatsApp</label>
                  <div className="input-wrapper14">
                    <Phone className="input-icon14" />
                    <input
                      type="text"
                      id="whatsapp"
                      name="whatsapp"
                      placeholder="Digite seu WhatsApp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group14">
                  <label htmlFor="senha">Senha</label>
                  <div className="input-wrapper14">
                    <Lock className="input-icon14" />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="senha"
                      name="senha"
                      placeholder="Crie uma senha"
                      value={formData.senha}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle14"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group14">
                  <label htmlFor="confirmarSenha">Confirmar Senha</label>
                  <div className="input-wrapper14">
                    <Lock className="input-icon14" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmarSenha"
                      name="confirmarSenha"
                      placeholder="Confirme sua senha"
                      value={formData.confirmarSenha}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle14"
                      onClick={toggleConfirmPasswordVisibility}
                      aria-label={showConfirmPassword ? "Esconder senha" : "Mostrar senha"}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="submit"
                className="cadastro-button14"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="button-loader14"></div>
                ) : (
                  <>
                    CRIAR CONTA
                    <UserPlus size={18} />
                  </>
                )}
              </motion.button>
            </form>

            <div className="cadastro-footer14">
              <p>
                Já tem uma conta?{" "}
                <Link to="/login" className="login-link14">
                  Faça login
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Cadastro

